import type { RouteLocationNormalized } from 'vue-router'

/** Мета-гард для роутов и пунктов меню */
export interface RouteMeta {
  /** Только этот tenant (например 'system') */
  tenant?: string
  /** Любой tenant, кроме этого (роли — в 'system' кастомных ролей нет, ACCESS O15) */
  notTenant?: string
  /** Требуемая роль (например 'admin') */
  role?: string
  /** Достаточно ЛЮБОЙ из ролей (например ['admin', 'supervisor']) */
  roleAnyOf?: string[]
  /**
   * Достаточно ЛЮБОЙ из капабилити (эпик ACCESS, `<resource>:<action>`). Когда права
   * загружены (`q.access.me`), решают они — так кастомная роль попадает на свои
   * страницы; до загрузки — роли выше (прежнее поведение).
   */
  capAnyOf?: string[]
  /** Нужны ВСЕ капабилити — страница без любой из них неработоспособна. */
  capAllOf?: string[]
  /**
   * Пункт меню (O27, `menu:<страница>`): без этой галочки у роли страница не видна в меню
   * и не открывается по ссылке, даже если права на её данные есть. Админ видит всё.
   */
  menu?: `menu:${string}`
}

/** Права пользователя для гарда (см. `useAccessStore`). */
export interface AccessSnapshot {
  readonly loaded: boolean
  readonly superuser: boolean
  readonly capabilities: ReadonlySet<string>
}

export const createMeta = (meta: RouteMeta): RouteMeta => meta

/** Проверка доступа: user vs meta */
export const checkAccess = (
  tenantId: string | null,
  roles: string[],
  meta?: RouteMeta,
  access?: AccessSnapshot,
): boolean => {
  if (!meta) return true
  if (meta.tenant && tenantId !== meta.tenant) return false
  if (meta.notTenant && tenantId === meta.notTenant) return false
  if (meta.menu && access?.loaded && !access.superuser && !access.capabilities.has(meta.menu)) return false
  const hasCapRule = meta.capAnyOf !== undefined || meta.capAllOf !== undefined
  if (hasCapRule && access?.loaded) {
    if (access.superuser) return true
    const has = (capability: string) => access.capabilities.has(capability)
    if (meta.capAnyOf && !meta.capAnyOf.some(has)) return false
    if (meta.capAllOf && !meta.capAllOf.every(has)) return false
    return true
  }
  if (meta.role && !roles.includes(meta.role)) return false
  if (meta.roleAnyOf && !meta.roleAnyOf.some((r) => roles.includes(r))) return false
  return true
}

/** Извлечь RouteMeta из route */
export const getRouteMeta = (route: RouteLocationNormalized): RouteMeta | undefined =>
  route.meta?.guard as RouteMeta | undefined
